import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MessageSquare, Pencil, Trash2, Loader2 } from 'lucide-react';

import type { CaseComment } from '@/api/urgent-cases';
import { patchCaseComment, deleteCaseComment } from '@/api/urgent-cases';
import { toast_success } from '@/libs/toast-wrappers';

import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface TrackingHistoryListProps {
  comments: CaseComment[];
  caseId: string;
  currentUserEmail?: string;
  onMutate: () => void;
  readOnly?: boolean;
}

export function TrackingHistoryList({
  comments,
  caseId,
  currentUserEmail,
  onMutate,
  readOnly = false,
}: TrackingHistoryListProps) {
  const { t } = useTranslation();
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  if (comments.length === 0) {
    return (
      <div className="rounded-lg border p-4 text-center text-muted-foreground">
        <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">{t('track-ack-history-empty')}</p>
      </div>
    );
  }

  const sortedComments = [...comments].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const handleEditStart = (comment: CaseComment) => {
    setEditingCommentId(comment.comment_id);
    setEditContent(comment.content);
  };

  const handleEditCancel = () => {
    setEditingCommentId(null);
    setEditContent('');
  };

  const handleEditSave = async (commentId: string) => {
    if (!editContent.trim()) return;
    setIsUpdating(true);
    try {
      await patchCaseComment(caseId, commentId, { content: editContent.trim() });
      toast_success(t('track-ack-comment-updated'));
      setEditingCommentId(null);
      setEditContent('');
      onMutate();
    } catch {
      // Error toast is already handled by the API function
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingCommentId) return;
    setIsDeleting(true);
    try {
      await deleteCaseComment(caseId, deletingCommentId);
      toast_success(t('track-ack-comment-deleted'));
      setDeletingCommentId(null);
      onMutate();
    } catch {
      // Error toast is already handled by the API function
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div>
        <h4 className="font-semibold mb-3">
          {t('track-ack-history-title', { count: comments.length })}
        </h4>
        <div className="space-y-3 max-h-[200px] overflow-y-auto">
          {sortedComments.map((comment) => {
            const isOwner = currentUserEmail && comment.user_email === currentUserEmail;
            const isEditing = editingCommentId === comment.comment_id;

            return (
              <div key={comment.comment_id} className="rounded-lg border p-3 text-sm space-y-2">
                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                      rows={3}
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      disabled={isUpdating}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleEditCancel}
                        disabled={isUpdating}
                      >
                        {t('cancel')}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleEditSave(comment.comment_id)}
                        disabled={isUpdating || !editContent.trim()}
                      >
                        {isUpdating && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                        {t('save')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-muted-foreground flex-1">{comment.content}</p>
                    {isOwner && !readOnly && (
                      <div className="flex gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleEditStart(comment)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-destructive hover:text-destructive"
                          onClick={() => setDeletingCommentId(comment.comment_id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t">
                  <span>{comment.user_email}</span>
                  <span>{new Date(comment.created_at).toLocaleString()}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <AlertDialog
        open={!!deletingCommentId}
        onOpenChange={(open) => {
          if (!open) setDeletingCommentId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('track-ack-delete-comment-title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('track-ack-delete-comment-description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={async (e) => {
                e.preventDefault();
                await handleDeleteConfirm();
              }}
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              {t('track-ack-delete-button')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
